import React, { useState, useEffect, useCallback  } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { editProperty, associateClientProperty, fetchActiveSellers } from '../../Redux/Actions/actions';
import style from './EditPropertyForm.module.css';
import { GoogleMap, LoadScript, Marker, StandaloneSearchBox  } from '@react-google-maps/api';
import ClientSelector from '../../Components/ClientSelector/ClientSelector';
import PropertyClients from '../../Components/PropertyClients/PropertyClients';
import VersionHistory from '../../Components/VersionHistory/VersionHistory';
import PropertyComments from '../../Components/PropertyComments/PropertyComments';
import logoEmpresa from '../../Assets/titulo.png'

const MyMapComponent = ({ initialLocation, onLocationChange }) => {
  const [mapLocation, setMapLocation] = useState({
    lat: parseFloat(initialLocation[0]),
    lng: parseFloat(initialLocation[1]),
  });
  const [searchBox, setSearchBox] = useState(null);

  const onLoad = useCallback((ref) => setSearchBox(ref), []);
  
  const onPlacesChanged = useCallback(() => {
    const places = searchBox.getPlaces();
    if (places && places.length > 0) {
      const place = places[0]; // Tomamos solo el primer lugar de la lista
      const location = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      setMapLocation(location);
      onLocationChange([location.lat.toFixed(6).toString(), location.lng.toFixed(6).toString()]); // Enviar array de strings
    }
  }, [searchBox, onLocationChange]);

  const handleMapClick = (e) => {
    const newLocation = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    };
    setMapLocation(newLocation);
    onLocationChange([newLocation.lat.toFixed(6).toString(), newLocation.lng.toFixed(6).toString()]); // Enviar array de strings
  };

  return (
    <LoadScript googleMapsApiKey="AIzaSyD5V2B-G8s7P7Hh6cZ6UyucD0n91y-JI3I" libraries={["places"]}>
      <StandaloneSearchBox onLoad={onLoad} onPlacesChanged={onPlacesChanged}>
        <input
          type="text"
          placeholder="Buscar dirección..."
          style={{
            boxSizing: `border-box`,
            border: `1px solid transparent`,
            width: `240px`,
            height: `32px`,
            padding: `0 12px`,
            borderRadius: `3px`,
            boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`,
            fontSize: `14px`,
            outline: `none`,
            textOverflow: `ellipses`,
          }}
        />
      </StandaloneSearchBox>
      <GoogleMap
        mapContainerStyle={{ height: '400px', width: '100%' }}
        zoom={13}
        center={mapLocation}
        onClick={handleMapClick}
      >
        <Marker
          position={mapLocation}
          draggable={true}
          onDragEnd={(e) => handleMapClick(e)}
        >
          <img src={logoEmpresa} alt="Logo de tu empresa" style={{ width: '50px', height: '50px' }} />
        </Marker>
      </GoogleMap>
    </LoadScript>
  );
};

const EditPropertyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate(); 
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const activeSellersRaw = useSelector(state => state.activeSellers);

  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [documentsToRemove, setDocumentsToRemove] = useState([]);
  const [originalSellerId, setOriginalSellerId] = useState(null);
  const [selectedSellerId, setSelectedSellerId] = useState('');
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [showPropertyClients, setShowPropertyClients] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitErrors, setSubmitErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userEmail = (user?.user?.mail || user?.mail || '').toLowerCase();
  const canEditSellerId = userEmail === 'arosa@byraices.com';
  const activeSellers = activeSellersRaw?.data || activeSellersRaw || [];

  const [formData, setFormData] = useState({
    propertyType: '',
    statusProperty: false,
    videoLink: '',
    currency: 'USD',
    price: '',
    currencyExpenses: 'USD',
    expenses: '',
    totalSquareMeters: '',
    coveredSquareMeters: '',
    semiCoveredSquareMeters: '',
    uncovered: '',
    land: '',
    age: '',
    commissionSellerType: '%',
    commissionBuyerType: '%',
    sellerCommission: '',
    buyerCommission: '',
    availableDate: '',
    expirationDate: '',
    location: ['', ''], // Inicializar como array de strings vacíos
    street: '',
    number: '',
    country: '',
    province: '',
    departments: '',
    locality: '',
    neighborhood: '',
    privateNeighborhood: '',
    environments: '',
    rooms: '',
    bathrooms: '',
    toilettes: '',
    garages: '',
    title: '',
    description: '',
    floorPlans: '',
    isForSale: false,
    isForRent: false,
    isFinished: false,
    isUnderDevelopment: false,
    documentation: [],  
    ownerName: '',
    ownerPhone: '',
    ownerEmail: '',
    propertyState: "",
    cantidad: '',
    
  });
  // Normalizar strings "true"/"false" a booleanos reales
  const toBooleans = (obj) => {
    const result = {};
    for (const [key, val] of Object.entries(obj)) {
      result[key] = val === "true" || val === true;
    }
    return result;
  };

  const [loading, setLoading] = useState(true);
  


  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`/property/${id}`);
        const propertyData = response.data.data;

        const incomingSellerId = propertyData?.sellerId ?? null;
        setOriginalSellerId(incomingSellerId);
        setSelectedSellerId(incomingSellerId !== null && incomingSellerId !== undefined ? String(incomingSellerId) : '');
  
        setFormData({
          price: propertyData.price,
          description: propertyData.description,
          propertyType: propertyData.propertyType,
          videoLink: propertyData.videoLink,
          currency: propertyData.currency,
          currencyExpenses: propertyData.currencyExpenses,
          expenses: propertyData.expenses,
          totalSquareMeters: propertyData.totalSquareMeters,
          coveredSquareMeters: propertyData.coveredSquareMeters,
          semiCoveredSquareMeters: propertyData.semiCoveredSquareMeters,
          uncovered: propertyData.uncovered,
          land: propertyData.land,
          age: propertyData.age,
          commissionSellerType: propertyData.commissionSellerType,
          commissionBuyerType: propertyData.commissionBuyerType,
          sellerCommission: propertyData.sellerCommission,
          buyerCommission: propertyData.buyerCommission,
          availableDate: propertyData.availableDate,
          expirationDate: propertyData.expirationDate,
          location: [
            propertyData.location[0].toString(), 
            propertyData.location[1].toString()
          ],
          street: propertyData.street,
          number: propertyData.number,
          country: propertyData.country,
          province: propertyData.province,
          departments: propertyData.departments,
          locality: propertyData.locality,
          neighborhood: propertyData.neighborhood,
          privateNeighborhood: propertyData.privateNeighborhood,
          environments: propertyData.environments,
          rooms: propertyData.rooms,
          bathrooms: propertyData.bathrooms,
          toilettes: propertyData.toilettes,
          garages: propertyData.garages,
          title: propertyData.title,
          floorPlans: propertyData.floorPlans,
          isForSale: propertyData.isForSale,
          isForRent: propertyData.isForRent,
          isFinished: propertyData.isFinished,
          isUnderDevelopment: propertyData.isUnderDevelopment,
          amenities: toBooleans(propertyData.amenities || {}),
          environmentsOptions: toBooleans(propertyData.environmentsOptions || {}),
          services: toBooleans(propertyData.services || {}),
          characteristics: toBooleans(propertyData.characteristics || {}),
          detailsProperty: toBooleans(propertyData.detailsProperty || {}),
          statusProperty: false,
          documentation: propertyData.documentation,
          propertyState: propertyData.propertyState,
          cantidad: propertyData.cantidad,
         
        });
        
        // Establecer el propietario actual de la propiedad si existe
        if (propertyData.clients && propertyData.clients.length > 0) {
          setSelectedOwner(propertyData.clients[0]);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching property:', error);
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id, dispatch]);

  useEffect(() => {
    if (canEditSellerId) {
      dispatch(fetchActiveSellers());
    }
  }, [canEditSellerId, dispatch]);


  
  const detailLabels = {
    exclusiveContract: 'Contrato Exclusivo',
    cartel: 'Cartel',
    financing: 'Financiamiento',
    suitableCredit: 'Apto a Credito',
    commercialSuitable: 'Apto Comercial',
    professionalSuitable: 'Apto Profesional',
    suitableForReducedMobility: 'Apto para Movilidad Reducida',
    pozo: 'Pozo',
    CountryOrPrivateNeighborhood: 'Country o barrio Privado',
  };
  
  const amenityLabels = {
    aireAcondicionado: 'Aire Acondicionado',
    portonAutomatico: 'Portón Automático',
    gimnasio: 'Gimnasio',
    losaRadiante: 'Losa Radiante',
    chimenea: 'Chimenea',
    hidromasaje: 'Hidromasaje',
    seguridad: 'Seguridad',
    pileta: 'Pileta',
    caldera: 'Caldera',
    businessCenter: 'Business Center',
    areaCine: 'Área de Cine',
    cisterna: 'Cisterna',
    laundry: 'Laundry',
    estacionamientoVisitas: 'Estacionamiento de Visitas',
    ascensor: 'Ascensor',
    salonUsosMultiples: 'Salón de Usos Múltiples',
    areaDeJuegosInfantiles: 'Área de Juegos Infantiles',
    canchaTenis: 'Cancha de Tenis',
    recepcion: 'Recepción',
    areasVerdes: 'Áreas Verdes',
    valetParking: 'Valet Parking',
    canchaBasquetbol: 'Cancha de Básquetbol',
    canchaFutbol: 'Cancha de Fútbol',
    canchaPaddle: 'Cancha de Paddle',
    solarium: 'Solarium',
    jardinDeInvierno: 'Jardín de Invierno',
    piletaCubierta: 'Pileta Cubierta',
    piletaClimatizada: 'Pileta Climatizada',
    sauna: 'Sauna',
    bar: 'Bar',
    calefaccion: 'Calefacción',
  };

  const characteristicLabels = {
    placard: 'Placard',
    parilla: 'Parrilla',
    desayunador: 'Desayunador', 
    orientacionSur: 'Orientación Sur',
    orientacionOeste: 'Orientación Oeste',
    orientacionNorte: 'Orientación Norte',
    orientacionEste: 'Orientación Este',
    accesoDeCocheraRampaFija: 'Acceso de Cochera - Rampa Fija',
    accesoDeCocheraRampaMovil: 'Acceso de Cochera - Rampa Móvil',
    accesoDeCocheraAscensor: 'Acceso de Cochera - Ascensor',
    accesoDeCocheraHorizontal: 'Acceso de Cochera - Horizontal',
    disposicionContrafrente: 'Disposición - Contrafrente',
    disposicionFrente: 'Disposición - Frente',
    disposicionInterno: 'Disposición - Interno',
    disposicionLateral: 'Disposición - Lateral',
    amoblado: 'Amoblado',
    orientacionNoroeste: 'Orientación Noroeste', 
    orientacionNoreste: 'Orientación Noreste',
    orientacionSuroeste: 'Orientación Suroeste',
    orientacionSureste: 'Orientación Sureste',
    deck: 'Deck',
    tipoDeCampoOtro: 'Tipo de Campo - Otro',
    tipoDeCampoFruticula: 'Tipo de Campo - Frutícola',
    tipoDeCampoAgricola: 'Tipo de Campo - Agrícola',
    tipoDeCampoChara: 'Tipo de Campo - Chacra',
    tipoDeCampoCriadero: 'Tipo de Campo - Criadero',
    tipoDeCampoTambero: 'Tipo de Campo - Tambero',
    tipoDeCampoFloricultura: 'Tipo de Campo - Floricultura',
    tipoDeCampoForestal: 'Tipo de Campo - Forestal',
    tipoDeCampoGanadero: 'Tipo de Campo - Ganadero',
    tipoDeCampoHaras: 'Tipo de Campo - Haras',
    bodegas: 'Bodegas',
    tipoDeBodegaComercial: 'Tipo de Bodega - Comercial',
    tipoDeBodegaNaveIndustrial: 'Tipo de Bodega - Nave Industrial',
    tipoDeBodegaAlmacen: 'Tipo de Bodega - Almacén',
    biblioteca: 'Biblioteca',
    galpon: 'Galpón',
    sotano: 'Sótano',
    baulera: 'Baulera',
    permiteMascota: 'Permite Mascota',
    aptoTuristico: 'Apto Turístico',
  };

  const environmentLabels = {
    dormitorio: 'Dormitorio',
    comedor: 'Comedor',
    vestidor: 'Vestidor',
    jardin: 'Jardín',
    baño: 'Baño',
    patio: 'Patio',
    terraza: 'Terraza',
    estudio: 'Estudio',
    lavadero: 'Lavadero',
    altillo: 'Altillo',
    playroom: 'Playroom',
    lobby: 'Lobby',
    quincho: 'Quincho',
    salaDeReuniones: 'Sala de Reuniones', 
    balcon: 'Balcón',
    pileta: 'Pileta',
    cocina: 'Cocina',
    toilette: 'Toilette',
    habitacion: 'Habitación',
    living: 'Living',
    otro: 'Otro',
  };

  const serviceLabels = {
    electricidad: 'Electricidad',
    agua: 'Agua',
    gas: 'Gas',
    internet: 'Internet',
    telefono: 'Teléfono',
    desagueCloacal: 'Desagüe Cloacal',
    televisionPorCable: 'Televisión por Cable',
    alarma: 'Alarma',
    televisionSatelital: 'Televisión Satelital',
    aguaCorriente: 'Agua Corriente',
  };

  const provincesArgentina = [
    // 'Buenos Aires',
    // 'Catamarca',
    // 'Chaco',
    // 'Chubut',
    // 'Córdoba',
    // 'Corrientes',
    // 'Entre Ríos',
    // 'Formosa',
    // 'Jujuy',
    // 'La Pampa',
    // 'La Rioja',
    'Mendoza',
    // 'Misiones',
    // 'Neuquén',
    // 'Río Negro',
    // 'Salta',
    // 'San Juan',
    // 'San Luis',
    // 'Santa Cruz',
    // 'Santa Fe',
    // 'Santiago del Estero',
    // 'Tierra del Fuego',
    // 'Tucumán'
  ];

  // Datos de departamentos por provincia
  const departmentsByProvince = {
    Mendoza: [
      'Capital',
      'General Alvear',
      'Godoy Cruz',
      'Guaymallén',
      'Junín',
      'La Paz',
      'Las Heras',
      'Lavalle',
      'Luján de Cuyo',
      'Maipú',
      'Malargüe',
      'Rivadavia',
      'San Carlos',
      'San Martín',
      'San Rafael',
      'Santa Rosa',
      'Tunuyán',
      'Tupungato'
    ],
    // Puedes agregar más provincias y sus respectivos departamentos aquí
  };

  // ========== VALIDACIÓN ==========
  const isLandType = (type) =>
    ['terrenos y lotes', 'campos y chacras'].includes(type?.toLowerCase?.() ?? '');

  const validationPatterns = {
    videoLink: { pattern: /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/, message: 'Debe ser una URL de YouTube válida', optional: true },
    price: { pattern: /^\d+$/, message: 'El precio solo debe contener números enteros' },
    coveredSquareMeters: { pattern: /^\d+$/, message: 'Superficie cubierta debe ser un número entero ≥ 0' },
    uncovered: { pattern: /^\d+$/, message: 'Superficie descubierta debe ser un número entero ≥ 0' },
    land: { pattern: /^\d+$/, message: 'Superficie del terreno debe ser un número entero ≥ 0' },
    age: { pattern: /^\d+$/, message: 'Antigüedad debe ser un número entero ≥ 0', optional: true },
    sellerCommission: { pattern: /^\d+(\.\d{1,2})?$/, message: 'Comisión del vendedor debe ser un número válido (ej: 3 o 3.50)', max: 999.99 },
    buyerCommission: { pattern: /^\d+(\.\d{1,2})?$/, message: 'Comisión del comprador debe ser un número válido (ej: 3 o 3.50)', max: 999.99 },
    environments: { pattern: /^\d+$/, message: 'Ambientes debe ser un número entero' },
    rooms: { pattern: /^\d+$/, message: 'Dormitorios debe ser un número entero' },
    bathrooms: { pattern: /^\d+$/, message: 'Baños debe ser un número entero' },
    toilettes: { pattern: /^\d+$/, message: 'Toilettes debe ser un número entero' },
    garages: { pattern: /^\d+$/, message: 'Cocheras debe ser un número entero' },
    floorPlans: { pattern: /^\d+$/, message: 'Pisos debe ser un número entero' },
  };

  const getFieldLabel = (field) => {
    const fieldLabels = {
      title: 'Título', description: 'Descripción', price: 'Precio',
      propertyType: 'Tipo de Propiedad', coveredSquareMeters: 'Metros Cuadrados Cubiertos',
      uncovered: 'Metros Cuadrados Descubiertos', land: 'Terreno',
      sellerCommission: 'Comisión del Vendedor', buyerCommission: 'Comisión del Comprador',
      documentation: 'Documentación', street: 'Calle', number: 'Número',
      country: 'País', province: 'Provincia', departments: 'Departamento',
      locality: 'Localidad', environments: 'Ambientes', rooms: 'Dormitorios',
      bathrooms: 'Baños', toilettes: 'Toilettes', garages: 'Cocheras',
      propertyState: 'Estado de la Propiedad', floorPlans: 'Pisos',
      isForSaleOrRent: 'Tipo de Operación (Venta o Alquiler)', videoLink: 'Video Link',
    };
    return fieldLabels[field] || field;
  };

  const validateForm = () => {
    const errors = [];
    const isLand = isLandType(formData.propertyType);
    const landOnlyExcluded = ['environments', 'rooms', 'bathrooms', 'toilettes', 'garages', 'floorPlans', 'coveredSquareMeters', 'uncovered'];
    const allRequiredFields = [
      'title', 'description', 'price', 'propertyType',
      'coveredSquareMeters', 'uncovered', 'land', 'sellerCommission', 'buyerCommission',
      'street', 'number', 'country', 'province', 'departments', 'locality',
      'environments', 'rooms', 'bathrooms', 'toilettes', 'garages', 'propertyState', 'floorPlans',
    ];
    const requiredFields = isLand
      ? allRequiredFields.filter(f => !landOnlyExcluded.includes(f))
      : allRequiredFields;

    for (const field of requiredFields) {
      const value = formData[field];
      if (!value || (typeof value === 'string' && !value.trim())) {
        errors.push({ field, message: `${getFieldLabel(field)} es obligatorio.` });
      }
    }

    if (!formData.isForSale && !formData.isForRent) {
      errors.push({ field: 'isForSaleOrRent', message: 'Debe seleccionar al menos un tipo de operación.' });
    }

    if (formData.title?.trim() && formData.title.trim().length < 3) {
      if (!errors.some(e => e.field === 'title'))
        errors.push({ field: 'title', message: 'El título debe tener al menos 3 caracteres.' });
    }

    for (const [field, rules] of Object.entries(validationPatterns)) {
      const value = String(formData[field] || '').trim();
      if (rules.optional && !value) continue;
      if (errors.some(e => e.field === field)) continue;
      if (value && !rules.pattern.test(value)) errors.push({ field, message: rules.message });
      if (value && rules.max !== undefined && Number(value) > rules.max) {
        if (!errors.some(e => e.field === field))
          errors.push({ field, message: `${getFieldLabel(field)} no puede superar ${rules.max}.` });
      }
    }

    const errorsMap = {};
    errors.forEach(err => { if (!errorsMap[err.field]) errorsMap[err.field] = err.message; });
    return { isValid: errors.length === 0, errors, errorsMap };
  };


  const handleChangePropertyType = (e) => {
    const { value } = e.target;
    setFormData({ ...formData, propertyType: value });
  };
  const handleAmenityChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      amenities: {
        ...prevData.amenities,
        [name]: checked,
      },
    }));
  };
  
  const handleEnvironmentOptionChange = (option) => {
    setFormData((prevData) => ({
      ...prevData,
      environmentsOptions: {
        ...prevData.environmentsOptions,
        [option]: !(prevData.environmentsOptions[option] === "true" || prevData.environmentsOptions[option] === true),
      },
    }));
  };
  
  const handleCharacteristicOptionChange = (option) => {
    setFormData((prevData) => ({
      ...prevData,
      characteristics: {
        ...prevData.characteristics,
        [option]: !(prevData.characteristics[option] === "true" || prevData.characteristics[option] === true),
      },
    }));
  };
  const handleDetailPropertyOptionChange = (option) => {
    setFormData((prevData) => ({
      ...prevData,
      detailsProperty: {
        ...prevData.detailsProperty,
        [option]: !(prevData.detailsProperty[option] === "true" || prevData.detailsProperty[option] === true),
      },
    }));
  };

const handleServiceOptionChange = (service) => {
  setFormData((prevData) => ({
    ...prevData,
    services: {
      ...prevData.services,
      [service]: !(prevData.services[service] === "true" || prevData.services[service] === true),
    },
  }));
};
  
  const handleLocationChange = (newLocation) => {
    setFormData(prevData => ({
      ...prevData,
      location: newLocation
    }));
  };

  const calculateTotalSquareMeters = (data) => {
    const { coveredSquareMeters, semiCoveredSquareMeters, uncovered, land } = data;
    return (parseFloat(coveredSquareMeters) || 0) +
           (parseFloat(semiCoveredSquareMeters) || 0) 
  };

  const [availableDepartments, setAvailableDepartments] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
  
    // Actualizar el estado del formulario con los nuevos valores
    let updatedFormData = { ...formData, [name]: value };
  
    // Si los campos modificados son metros cuadrados, recalcular el total
    if (name === "coveredSquareMeters" || name === "semiCoveredSquareMeters" || name === "uncovered" || name === "land") {
      const totalSquareMeters = calculateTotalSquareMeters(updatedFormData);
      updatedFormData = { ...updatedFormData, totalSquareMeters };
    }

    if (name === 'country') {
      // Si cambia el país, reiniciar el campo 'province' si no es Argentina
      updatedFormData = {
        ...updatedFormData,
        province: value === 'Argentina' ? formData.province : '', // Solo mantener la provincia si es Argentina
      };
    }

    if (name === 'province') {
      if (departmentsByProvince[value]) {
        setAvailableDepartments(departmentsByProvince[value]);
      } else {
        setAvailableDepartments([]);
      }
    }
  
    // Actualizar el estado del formulario
    setFormData(updatedFormData);
  };
  


  const handleDocumentationChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setSelectedDocuments((prev) => [...prev, ...files]);
    // Limpiar el value para permitir re-seleccionar el mismo archivo si se eliminó por error
    e.target.value = '';
  };

  const handleRemoveNewDocument = (index) => {
    setSelectedDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const normalizeDocumentUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    return url.split('?')[0].split('#')[0].trim();
  };

  const handleRemoveExistingDocument = (docUrl) => {
    const normalizedUrl = normalizeDocumentUrl(docUrl);
    if (!normalizedUrl) return;
    setDocumentsToRemove((prev) => {
      const alreadyMarked = prev.some((u) => normalizeDocumentUrl(u) === normalizedUrl);
      return alreadyMarked ? prev : [...prev, docUrl];
    });
  };

  const handleUndoRemoveExisting = (docUrl) => {
    const normalizedUrl = normalizeDocumentUrl(docUrl);
    setDocumentsToRemove((prev) => prev.filter((u) => normalizeDocumentUrl(u) !== normalizedUrl));
  };

  const getFileNameFromUrl = (url) => {
    if (!url || typeof url !== 'string') return 'documento';
    try {
      const clean = url.split('?')[0].split('#')[0];
      const last = clean.substring(clean.lastIndexOf('/') + 1);
      return decodeURIComponent(last) || 'documento';
    } catch {
      return 'documento';
    }
  };

  const getFileExtension = (nameOrUrl) => {
    if (!nameOrUrl) return '';
    const name = getFileNameFromUrl(nameOrUrl);
    const dot = name.lastIndexOf('.');
    if (dot === -1 || dot === name.length - 1) return '';
    return name.substring(dot + 1).toUpperCase();
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ========== VALIDACIÓN CLIENT-SIDE ==========
    setFieldErrors({});
    setSubmitErrors([]);
    const { isValid: formIsValid, errors: validationErrors, errorsMap } = validateForm();
    if (!formIsValid) {
      setFieldErrors(errorsMap);
      setSubmitErrors(validationErrors);
      toast.error(
        `Se encontraron ${validationErrors.length} error(es) de validación. Revise los campos marcados en rojo.`,
        { autoClose: 6000 }
      );
      const firstErrorField = validationErrors[0]?.field;
      if (firstErrorField) {
        setTimeout(() => {
          const el = document.querySelector(`[name="${firstErrorField}"]`);
          if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
        }, 100);
      }
      return;
    }
    setIsSubmitting(true);
  
    // Crear un nuevo FormData para la solicitud de edición
    const formDataToSend = new FormData();
  
    // Añadir los campos de texto y booleanos al FormData
    formDataToSend.append('propertyType', formData.propertyType);
    formDataToSend.append('statusProperty', formData.statusProperty);
    formDataToSend.append('videoLink', formData.videoLink);
    formDataToSend.append('currency', formData.currency);
    formDataToSend.append('price', formData.price);
    formDataToSend.append('currencyExpenses', formData.currencyExpenses);
    formDataToSend.append('expenses', formData.expenses);
    formDataToSend.append('totalSquareMeters', formData.totalSquareMeters);
    formDataToSend.append('coveredSquareMeters', formData.coveredSquareMeters);
    formDataToSend.append('semiCoveredSquareMeters', formData.semiCoveredSquareMeters);
    formDataToSend.append('uncovered', isLandType(formData.propertyType) ? (formData.land || '0') : formData.uncovered);
    formDataToSend.append('land', formData.land);
    formDataToSend.append('age', formData.age);
    formDataToSend.append('commissionSellerType', formData.commissionSellerType);
    formDataToSend.append('commissionBuyerType', formData.commissionBuyerType);
    formDataToSend.append('sellerCommission', formData.sellerCommission);
    formDataToSend.append('buyerCommission', formData.buyerCommission);
    formDataToSend.append('availableDate', formData.availableDate);
    formDataToSend.append('expirationDate', formData.expirationDate);
    formDataToSend.append('street', formData.street);
    formDataToSend.append('number', formData.number);
    formDataToSend.append('country', formData.country);
    formDataToSend.append('province', formData.province);
    formDataToSend.append('departments', formData.departments);
    formDataToSend.append('locality', formData.locality);
    formDataToSend.append('neighborhood', formData.neighborhood);
    formDataToSend.append('privateNeighborhood', formData.privateNeighborhood);
    formDataToSend.append('title', formData.title);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('floorPlans', formData.floorPlans);
    formDataToSend.append('isForSale', formData.isForSale);
    formDataToSend.append('isForRent', formData.isForRent);
    formDataToSend.append('isFinished', formData.isFinished);
    formDataToSend.append('isUnderDevelopment', formData.isUnderDevelopment);
    formDataToSend.append('environments', formData.environments);
    formDataToSend.append('rooms', formData.rooms);
    formDataToSend.append('bathrooms', formData.bathrooms);
    formDataToSend.append('toilettes', formData.toilettes);
    formDataToSend.append('garages', formData.garages);
    formDataToSend.append('propertyState', formData.propertyState);
    formDataToSend.append('cantidad', formData.cantidad);
    const sellerIdToSend = canEditSellerId
      ? (selectedSellerId !== '' ? selectedSellerId : null)
      : (originalSellerId ?? null);

    if (sellerIdToSend !== null && sellerIdToSend !== undefined && sellerIdToSend !== '') {
      formDataToSend.append('sellerId', sellerIdToSend);
    }
    if (formData.motivo) {
      formDataToSend.append('motivo', formData.motivo);
    }

 
    // Guardar la ubicación como un array de coordenadas (latitud, longitud)

      formDataToSend.append('location[]', formData.location[0]); // Latitud
      formDataToSend.append('location[]', formData.location[1]); // Longitud

  
    // Agregar los campos complejos como JSON para evitar problemas de encoding con caracteres especiales (ej: ñ en "baño")
    formDataToSend.append('amenities', JSON.stringify(formData.amenities || {}));
    formDataToSend.append('environmentsOptions', JSON.stringify(formData.environmentsOptions || {}));
    formDataToSend.append('services', JSON.stringify(formData.services || {}));
    formDataToSend.append('characteristics', JSON.stringify(formData.characteristics || {}));
    formDataToSend.append('detailsProperty', JSON.stringify(formData.detailsProperty || {}));

   // 📄 Agregar documentos correctamente
for (const documentation of selectedDocuments) {
  formDataToSend.append('documentation', documentation);  // Asegurar array en la solicitud
}
    // Documentos existentes marcados para eliminar
    if (documentsToRemove.length > 0) {
      const docsToRemove = documentsToRemove
        .map((url) => (typeof url === 'string' ? url.trim() : ''))
        .filter(Boolean);

      if (docsToRemove.length > 0) {
        // El backend espera las URLs exactas existentes en property.documentation
        formDataToSend.append('documentationToRemove', JSON.stringify(docsToRemove));
      }
    }
    try {
      // Usando Redux para editar la propiedad
      const result = await dispatch(editProperty(id, formDataToSend));
      
      // Asociar el propietario seleccionado si existe
      if (selectedOwner) {
        try {
          await dispatch(associateClientProperty(selectedOwner.id, id));
        } catch (clientError) {
          console.warn('Error al asociar propietario:', clientError);
        }
      }
      
      if (result?.versionCreada) {
        toast.success('Propiedad actualizada. Versión registrada, pendiente de aprobación.', { autoClose: 4000 });
      } else {
        toast.success('Propiedad actualizada correctamente.', { autoClose: 4000 });
      }

      if (documentsToRemove.length > 0) {
        const toRemoveSet = new Set(documentsToRemove.map(normalizeDocumentUrl).filter(Boolean));
        setFormData((prev) => ({
          ...prev,
          documentation: Array.isArray(prev.documentation)
            ? prev.documentation.filter((doc) => !toRemoveSet.has(normalizeDocumentUrl(doc)))
            : prev.documentation,
        }));
        setDocumentsToRemove([]);
      }

      setSelectedDocuments([]);
    } catch (error) {
      console.error('Error al actualizar la propiedad:', error);
      const serverError = error?.response?.data;
      if (serverError?.errors?.length > 0) {
        const serverErrorsMap = {};
        serverError.errors.forEach(err => { serverErrorsMap[err.field] = err.message; });
        setFieldErrors(serverErrorsMap);
        setSubmitErrors(serverError.errors);
        toast.error(serverError.message || 'Error al guardar la propiedad.', { autoClose: 6000 });
        const firstField = serverError.errors[0]?.field;
        if (firstField) {
          setTimeout(() => {
            const el = document.querySelector(`[name="${firstField}"]`);
            if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
          }, 100);
        }
      } else {
        toast.error(serverError?.message || 'Error al actualizar la propiedad. Intentá de nuevo.', { autoClose: 5000 });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  

  const handleSaleButtonClick = () => {
    const currentIsForSale = formData.isForSale;
    setFormData({
      ...formData,
      isForSale: true,
      isForRent: false,
    });
    if (!currentIsForSale) {
      setFormData(prevFormData => ({
        ...prevFormData,
        isForSale: true,
      }));
    }
  };

  const handleRentButtonClick = () => {
    const currentIsForSale = formData.isForSale;
    setFormData({
      ...formData,
      isForSale: false,
      isForRent: true,
    });
    if (currentIsForSale) {
      setFormData(prevFormData => ({
        ...prevFormData,
        isForSale: false,
      }));
    }
  };


  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   const updatedFormData = { ...formData, statusProperty: false };
  //   dispatch(editProperty(id, updatedFormData));
  // };

  if (loading) {
    return <div>Loading...</div>;
  }


  // Componente del mapa

  console.log(formData)

  return (
    <div className={style.container}>
      <h1 className={style.pageTitle}>Editar Propiedad</h1>
      <p className={style.pageSubtitle}>Modificá los datos de tu propiedad y guardá los cambios</p>
      <form onSubmit={handleSubmit} className={style.form}>

      {canEditSellerId && (
        <div className={style.formGroup}>
          <h2 className={style.title}>Agente asignado (sellerId)</h2>
          <select
            className={style.propertyTypeDropdown}
            value={selectedSellerId}
            onChange={(e) => setSelectedSellerId(e.target.value)}
          >
            <option value="">Seleccionar agente activo</option>
            {Array.isArray(activeSellers) && activeSellers.map((seller) => (
              <option key={seller.id} value={seller.id}>
                {seller.name} {seller.last_name ? seller.last_name : ''} (ID: {seller.id})
              </option>
            ))}
          </select>
        </div>
      )}
      
      {/* Selector de Propietario */}
      <div className={style.formGroup}>
        <h2 className={style.title}>Propietario de la Propiedad</h2>
        {user && user.userId ? (
          <ClientSelector 
            selectedClients={selectedOwner ? [selectedOwner] : []}
            onSelectionChange={(clients) => setSelectedOwner(clients[0] || null)}
            sellerId={user.userId}
            showCategoryFilter
            allowCreate
          />
        ) : (
          <div>Cargando información del usuario...</div>
        )}
      </div>
      
      {/* Botón para ver clientes interesados */}
      <div className={style.formGroup}>
        <button
          type="button"
          onClick={() => setShowPropertyClients(true)}
          className={style.clientsButton}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          👥 Ver Clientes Interesados
        </button>
      </div>
      
      <h2 className={style.title}>Tipo de operación</h2>
      <div className={style.operationContainer}>
          <button
            type="button"
            onClick={handleSaleButtonClick}
            className={`${style.operationButton} ${style.sale} ${formData.isForSale ? style.selected : ''}`}
          >
            Venta
          </button>
          <button
            type="button"
            onClick={handleRentButtonClick}
            className={`${style.operationButton} ${style.rent} ${formData.isForRent ? style.selected : ''}`}
          >
            Alquiler
          </button>
        </div>
      {fieldErrors.isForSaleOrRent && <p className={style.errorMessage}>{fieldErrors.isForSaleOrRent}</p>}
      <div className={style.formGroup}>
        <label>
        <h2 className={style.title}>Tipo de propiedad</h2>
          <select
           className={`${style.propertyTypeDropdown} ${fieldErrors.propertyType ? style.inputError : ''}`}
            value={formData.propertyType}
            onChange={handleChangePropertyType}
            name="propertyType"
            >
            <option value="departamento">Departamento</option>
            <option value="casa">Casa</option>
            <option value="ph">PH</option>
            <option value="local">Local</option>
            <option value="terrenos y lotes">Terrenos y lotes</option>
            <option value="campos y chacras">Campos y chacras</option>
            <option value="fondo de comercio">Fondo de comercio</option>
            <option value="cochera">Cochera</option>
            <option value="oficina">Oficina</option>
            <option value="galpon">Galpón</option>
            <option value="quinta">Quinta</option>
            <option value="otro">Otros</option>
          </select>
          {fieldErrors.propertyType && <p className={style.errorMessage}>{fieldErrors.propertyType}</p>}
        </label>
        <div  className={style.formGroup}>
        {["departamento", "ph", "local", "terrenos y lotes", "cochera", "oficina", "otro"].includes(formData.propertyType) && (
  <div className={style.formGroup}>
    <h2 className={style.title}>Cantidad</h2>
    <input
      type="number"
      name="cantidad"
      value={formData.cantidad}
      onChange={(e) => setFormData({ ...formData, cantidad: e.target.value })}
      className={style.inputText}
    />
  </div>
)}

        <h2 className={`${style.title}`}>Precio</h2>
        <div className={style.priceInputContainer}>
          <select
            id="currency"
            name="currency"
            value={formData.currency}
            onChange={handleChange}
            className={`${style.selectInput}`}
          >
            <option value="USD">USD</option>
            <option value="ARG">ARG</option>
          </select>
          <input
            type="text"
            name="price"
            value={formData.price}
            onChange={handleChange}
            onKeyPress={(e) => {
              // Solo permitir números, punto decimal y backspace
              if (!/[0-9]/.test(e.key) && e.key !== 'Backspace') {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              // Filtrar caracteres no válidos incluyendo 'e' y '+/-'
              e.target.value = e.target.value.replace(/[^0-9.]/g, '');
            }}
            className={`${style.inputNumber} ${fieldErrors.price ? style.inputError : ''}`}
          />
        </div>
        {fieldErrors.price && <p className={style.errorMessage}>{fieldErrors.price}</p>}
        </div>
        <div className={style.formGroup}>
          <h2 className={style.title}>Expensas</h2>
          <div className={style.priceInputContainer}>
            <select id="currencyExpenses" name="currencyExpenses" value={formData.currencyExpenses} onChange={handleChange} className={`${style.selectInput}`}>
              <option value="USD">USD</option>
              <option value="ARG">ARG</option>
            </select>
            <input type="number" name="expenses" value={formData.expenses} onChange={handleChange} className={`${style.inputNumber}`}/>
          </div>
        </div>
    <div className={style.formGroup}>
      <h2 className={style.title}>Comision del Vendedor</h2>
      <div className={style.priceInputContainer}>
        <select id="commissionSellerType" name="commissionSellerType" value={formData.commissionSellerType} onChange={handleChange}  className={`${style.selectInput}`}>
          <option value="%">%</option>
          <option value="Fijo">Fijo</option>
        </select>
        <input type="number" name="sellerCommission" value={formData.sellerCommission} onChange={handleChange}  className={`${style.inputNumber} ${fieldErrors.sellerCommission ? style.inputError : ''}`}/>
      </div>
      {fieldErrors.sellerCommission && <p className={style.errorMessage}>{fieldErrors.sellerCommission}</p>}
    </div>

 <div className={style.formGroup}>
      <h2 className={style.title}>Comision del Comprador</h2>
      <div className={style.priceInputContainer}>
        <select id="commissionBuyerType" name="commissionBuyerType" value={formData.commissionBuyerType} onChange={handleChange} className={`${style.selectInput}`}>
          <option value="%">%</option>
          <option value="Fijo">Fijo</option>
        </select>
        <input type="number" name="buyerCommission" value={formData.buyerCommission} onChange={handleChange} className={`${style.inputNumber} ${fieldErrors.buyerCommission ? style.inputError : ''}`}/>
      </div>
      {fieldErrors.buyerCommission && <p className={style.errorMessage}>{fieldErrors.buyerCommission}</p>}
    </div>
 <div className={style.formGroup}>
    <h2 className={style.title}>Fecha disponible</h2>
      <input type="date" name="availableDate" value={formData.availableDate} onChange={handleChange} />
      </div>
 <div className={style.formGroup}>
          <h2 className={style.title}>Fecha de vencimiento</h2>
      <input type="date" name="expirationDate" value={formData.expirationDate} onChange={handleChange} />
 </div>
 <div className={style.formUbication}>
      <h2 className={style.title}>Ubicación</h2>
  <div className={style.formRow}>
    <div className={style.formColumn}>
      <div className={style.inputGroup}>
        <h3 className={style.subtitle}>Calle</h3>
        <input type="text" name="street" value={formData.street} onChange={handleChange} className={`${style.inputText} ${fieldErrors.street ? style.inputError : ''}`} />
        {fieldErrors.street && <p className={style.errorMessage}>{fieldErrors.street}</p>}
      </div>
      <div className={style.inputGroup}>
        <h3 className={style.subtitle}>Numeración</h3>
        <input type="text" name="number" value={formData.number} onChange={handleChange} className={`${style.inputText} ${fieldErrors.number ? style.inputError : ''}`} />
        {fieldErrors.number && <p className={style.errorMessage}>{fieldErrors.number}</p>}
      </div>
    </div>
    <div className={style.formColumn}>
      <div className={style.inputGroup}>
      <h3 className={style.subtitle}>País</h3>
            <select
              name="country"
              value={formData.country}
              onChange={handleChange}
              className={`${style.inputText} ${fieldErrors.country ? style.inputError : ''}`}
            >
              <option value="">Selecciona un país</option>
              <option value="Argentina">Argentina</option>
              {/* Añade más países según sea necesario */}
            </select>
            {fieldErrors.country && <p className={style.errorMessage}>{fieldErrors.country}</p>}
      </div>
      <div className={style.inputGroup}>
      <h3 className={style.subtitle}>Provincia</h3>
            <select
              name="province"
              value={formData.province}
              onChange={handleChange}
              className={`${style.inputText} ${fieldErrors.province ? style.inputError : ''}`}
              disabled={formData.country !== 'Argentina'} // Deshabilitar si no es Argentina
            >
              <option value="">Selecciona una provincia</option>
              {provincesArgentina.map((province, index) => (
                <option key={index} value={province}>
                  {province}
                </option>
              ))}
            </select>
            {fieldErrors.province && <p className={style.errorMessage}>{fieldErrors.province}</p>}
      </div>
    </div>
  </div>
  <div className={style.formRow}>
    <div className={style.formColumn}>
      <div className={style.inputGroup}>
      <h3 className={style.subtitle}>Departamento</h3>
            <select
              name="departments"
              value={formData.departments}
              onChange={handleChange}
              className={`${style.inputText} ${fieldErrors.departments ? style.inputError : ''}`}
              disabled={!formData.province || availableDepartments.length === 0}
            >
              <option value="">Seleccione un departamento</option>
              {availableDepartments.map((department) => (
                <option key={department} value={department}>
                  {department}
                </option>
              ))}
            </select>
            {fieldErrors.departments && <p className={style.errorMessage}>{fieldErrors.departments}</p>}
      </div>
      <div className={style.inputGroup}>
        <h3 className={style.subtitle}>Localidad</h3>
        <input type="text" name="locality" value={formData.locality} onChange={handleChange} className={`${style.inputText} ${fieldErrors.locality ? style.inputError : ''}`} />
        {fieldErrors.locality && <p className={style.errorMessage}>{fieldErrors.locality}</p>}
      </div>
    </div>
    <div className={style.formColumn}>
      <div className={style.inputGroup}>
        <h3 className={style.subtitle}>Barrio</h3>
        <input type="text" name="neighborhood" value={formData.neighborhood} onChange={handleChange} className={style.inputText} />
      </div>
      <div className={style.inputGroup}>
        <h3 className={style.subtitle}>Barrio privado</h3>
        <input type="text" name="privateNeighborhood" value={formData.privateNeighborhood} onChange={handleChange} className={style.inputText} />
      </div>
    </div>
  </div>
  <MyMapComponent initialLocation={formData.location} onLocationChange={handleLocationChange} />
  </div>
        </div>

{!isLandType(formData.propertyType) && (
<>
<div className={style.formGroup}>
  <h2 className={style.title}>Ambientes</h2>
  <input
    type="number"
    name="environments"
    value={formData.environments}
    onChange={handleChange}
    className={`${style.inputText} ${fieldErrors.environments ? style.inputError : ''}`}
  />
  {fieldErrors.environments && <p className={style.errorMessage}>{fieldErrors.environments}</p>}
</div>

<div className={style.formGroup}>
  <h2 className={style.title}>Dormitorios</h2>
  <input
    type="number"
    name="rooms"
    value={formData.rooms}
    onChange={handleChange}
    className={`${style.inputText} ${fieldErrors.rooms ? style.inputError : ''}`}
  />
  {fieldErrors.rooms && <p className={style.errorMessage}>{fieldErrors.rooms}</p>}
</div>

<div className={style.formGroup}>
  <h2 className={style.title}>Baños</h2>
  <input
    type="number"
    name="bathrooms"
    value={formData.bathrooms}
    onChange={handleChange}
    className={`${style.inputText} ${fieldErrors.bathrooms ? style.inputError : ''}`}
  />
  {fieldErrors.bathrooms && <p className={style.errorMessage}>{fieldErrors.bathrooms}</p>}
</div>

<div className={style.formGroup}>
  <h2 className={style.title}>Toilettes</h2>
  <input
    type="number"
    name="toilettes"
    value={formData.toilettes}
    onChange={handleChange}
    className={`${style.inputText} ${fieldErrors.toilettes ? style.inputError : ''}`}
  />
  {fieldErrors.toilettes && <p className={style.errorMessage}>{fieldErrors.toilettes}</p>}
</div>

<div className={style.formGroup}>
  <h2 className={style.title}>Cocheras</h2>
  <input
    type="number"
    name="garages"
    value={formData.garages}
    onChange={handleChange}
    className={`${style.inputText} ${fieldErrors.garages ? style.inputError : ''}`}
  />
  {fieldErrors.garages && <p className={style.errorMessage}>{fieldErrors.garages}</p>}
</div>

<div className={style.formGroup}>
  <h2 className={style.title}>Pisos</h2>
  <input
    type="number"
    name="floorPlans"
    value={formData.floorPlans}
    onChange={handleChange}
    className={`${style.inputText} ${fieldErrors.floorPlans ? style.inputError : ''}`}
  />
  {fieldErrors.floorPlans && <p className={style.errorMessage}>{fieldErrors.floorPlans}</p>}
</div>
</>)}

<div className={style.formGroup}>
  <h2 className={style.title}>Estado de la Propiedad</h2>
  <select
    name="propertyState"
    value={formData.propertyState}
    onChange={handleChange}
    className={`${style.inputText} ${fieldErrors.propertyState ? style.inputError : ''}`}
  >
    <option value="">Selecciona una opción</option>
    <option value="aEstrenar">A Estrenar</option>
    <option value="enConstruccion">En Construcción</option>
    <option value="refaccionado">Refaccionado</option>
    <option value="aRefaccionar">A Refaccionar</option>
    <option value="excelente">Excelente</option>
    <option value="muyBueno">Muy Bueno</option>
    <option value="bueno">Bueno</option>
    <option value="regular">Regular</option>
  </select>
  {fieldErrors.propertyState && <p className={style.errorMessage}>{fieldErrors.propertyState}</p>}
</div>

<h2 className={style.title}>Superficie</h2>
<p className={style.subtitleText}>{isLandType(formData.propertyType) ? 'Complete el metraje del terreno. Cubierto y Descubierto se calculan automáticamente.' : 'Completar todos los datos con su respectivo metraje y en caso de no poseer colocar "0".'}</p>
<div className={style.formGroupMedidas}>
  <div className={style.inputGroup}>
    {!isLandType(formData.propertyType) && (
    <div className={style.inputContainer}>
      <h3 className={style.subtitleM}>Cubierto (m²)</h3>
      <input
        type="number"
        name="coveredSquareMeters"
        value={formData.coveredSquareMeters}
        onChange={handleChange}
        className={`${style.inputM} ${fieldErrors.coveredSquareMeters ? style.inputError : ''}`}
      />
      {fieldErrors.coveredSquareMeters && <p className={style.errorMessage}>{fieldErrors.coveredSquareMeters}</p>}
    </div>
    )}
    {!isLandType(formData.propertyType) && (
    <div className={style.inputContainer}>
      <h3 className={style.subtitleM}>Descubierto (m²)</h3>
      <input
        type="number"
        name="uncovered"
        value={formData.uncovered}
        onChange={handleChange}
        className={`${style.inputM} ${fieldErrors.uncovered ? style.inputError : ''}`}
      />
      {fieldErrors.uncovered && <p className={style.errorMessage}>{fieldErrors.uncovered}</p>}
    </div>
    )}
    <div className={style.inputContainer}>
      <h3 className={style.subtitleM}>Terreno (m²)</h3>
      <input
        type="number"
        name="land"
        value={formData.land}
        onChange={handleChange}
        className={`${style.inputM} ${fieldErrors.land ? style.inputError : ''}`}
      />
      {fieldErrors.land && <p className={style.errorMessage}>{fieldErrors.land}</p>}
    </div>
    <div className={style.inputContainer}>
      <h2 className={style.label}>Total (m²): {formData.totalSquareMeters}</h2>
    </div>
  </div>
</div>

<div className={style.formGroup}>
      <h2 className={style.title}>Antiguedad</h2>
      <input className={`${style.inputAge} ${fieldErrors.age ? style.inputError : ''}`} type="number" name="age" placeholder='Ej: 2010' value={formData.age} onChange={handleChange} />
      {fieldErrors.age && <p className={style.errorMessage}>{fieldErrors.age}</p>}
    </div>
    <h2 className={style.title}>Detalles de la propiedad</h2>
<div className={style.formGroupChechbox}>
  {Object.entries(formData.detailsProperty || {}).map(([detail, value]) => (
    <div key={detail}>
      <input
        type="checkbox"
        id={detail}
        checked={!!value}
        onChange={() => handleDetailPropertyOptionChange(detail)}
      />
      <label htmlFor={detail}>{detailLabels[detail]}</label>
    </div>
  ))}
</div>

<h2 className={style.title}>Comodidades</h2>
<div className={style.formGroupChechbox}>
  {Object.entries(formData.amenities || {}).map(([amenity, value]) => (
    <div key={amenity} className={style.checkboxContainer}>
      <label>
        <input
          type="checkbox"
          name={amenity}
          checked={!!value}
          onChange={handleAmenityChange}
        />
        {amenityLabels[amenity]}
      </label>
    </div>
  ))}
</div>

<h2 className={style.title}>Características</h2>
<div className={style.formGroupChechbox}>
  {Object.entries(formData.characteristics || {}).map(([option, value]) => (
    <div key={option} className={style.checkboxContainer}>
      <input
        type="checkbox"
        id={option}
        checked={!!value}
        onChange={() => handleCharacteristicOptionChange(option)}
      />
      <label htmlFor={option}>{characteristicLabels[option]}</label>
    </div>
  ))}
</div>

<h2 className={style.title}>Ambientes</h2>
<div className={style.formGroupChechbox}>
  {Object.entries(formData.environmentsOptions || {}).map(([option, value]) => (
    <div key={option}>
      <input
        type="checkbox"
        id={option}
        checked={!!value}
        onChange={() => handleEnvironmentOptionChange(option)}
      />
      <label htmlFor={option}>{environmentLabels[option]}</label>
    </div>
  ))}
</div>

<h2 className={style.title}>Servicios</h2>
<div className={style.formGroupChechbox}>
  {Object.entries(formData.services || {}).map(([service, value]) => (
    <div key={service}>
      <input
        type="checkbox"
        id={service}
        checked={!!value}
        onChange={() => handleServiceOptionChange(service)}
      />
      <label htmlFor={service}>{serviceLabels[service]}</label>
    </div>
  ))}
</div>

<div className={style.formGroup}>

    <label>
     <h2 className={style.title}>Titulo</h2>
      <input className={`${style.inputText} ${fieldErrors.title ? style.inputError : ''}`} type="text" name="title" placeholder='Ingrese un titulo' value={formData.title} onChange={handleChange} />
      {fieldErrors.title && <p className={style.errorMessage}>{fieldErrors.title}</p>}
    </label>
 </div>
 <div className={style.formGroup}>

    <label>
     <h2 className={style.title}>Descripcion</h2>
      <textarea className={`${style.inputDescription} ${fieldErrors.description ? style.inputError : ''}`} type="text" name="description" placeholder='Ingrese una descripcion' cols="30" rows="5" value={formData.description} onChange={handleChange} />
      {fieldErrors.description && <p className={style.errorMessage}>{fieldErrors.description}</p>}
    </label>
 </div>
 <div className={style.formGroup}>
 <h2>Editar Imágenes</h2>
 <button
        type="button"
        className={style.editImagesButton}
        onClick={() => navigate(`/edit-images/${id}`)} // Redirigir a /edit-images/:id
      >
        Editar Imágenes
      </button>
  


<h2 className={style.title}>
Video Link
</h2>
   
    <input className={`${style.inputText} ${fieldErrors.videoLink ? style.inputError : ''}`} type="text" name="videoLink" placeholder='Link de YouTube' value={formData.videoLink} onChange={handleChange} />
    {fieldErrors.videoLink && <p className={style.errorMessage}>{fieldErrors.videoLink}</p>}
    <h1>Documentaciónssss</h1>

    {/* Documentos ya guardados */}
    <h3 className={style.subtitle}>Documentos actuales</h3>
    {Array.isArray(formData.documentation) && formData.documentation.length > 0 ? (
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px 0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {formData.documentation.map((docUrl, index) => {
          const isMarked = documentsToRemove.some((u) => normalizeDocumentUrl(u) === normalizeDocumentUrl(docUrl));
          const fileName = getFileNameFromUrl(docUrl);
          const ext = getFileExtension(docUrl);
          return (
            <li
              key={`existing-${index}-${docUrl}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px 10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: isMarked ? '#fdecea' : '#fafafa',
                opacity: isMarked ? 0.7 : 1,
              }}
            >
              <span style={{ fontSize: '18px' }}>📄</span>
              <a
                href={docUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flex: 1,
                  textDecoration: isMarked ? 'line-through' : 'none',
                  color: isMarked ? '#888' : '#0366d6',
                  wordBreak: 'break-all',
                }}
              >
                {fileName}
              </a>
              {ext && (
                <span style={{ fontSize: '12px', background: '#eee', padding: '2px 6px', borderRadius: '3px' }}>
                  {ext}
                </span>
              )}
              {isMarked ? (
                <button
                  type="button"
                  onClick={() => handleUndoRemoveExisting(docUrl)}
                  style={{ background: '#6c757d', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '3px', cursor: 'pointer' }}
                >
                  Deshacer
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleRemoveExistingDocument(docUrl)}
                  title="Eliminar documento"
                  style={{ background: '#dc3545', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '3px', cursor: 'pointer' }}
                >
                  ✕
                </button>
              )}
            </li>
          );
        })}
      </ul>
    ) : (
      <p style={{ color: '#777', fontStyle: 'italic', margin: '0 0 12px 0' }}>No hay documentos guardados.</p>
    )}

    {/* Documentos nuevos en cola */}
    <h3 className={style.subtitle}>Documentos a subir</h3>
    {selectedDocuments.length > 0 ? (
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px 0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {selectedDocuments.map((file, index) => {
          const ext = getFileExtension(file.name) || (file.type ? file.type.split('/').pop().toUpperCase() : '');
          return (
            <li
              key={`new-${index}-${file.name}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px 10px',
                border: '1px dashed #4caf50',
                borderRadius: '4px',
                background: '#f1faf1',
              }}
            >
              <span style={{ fontSize: '18px' }}>📎</span>
              <span style={{ flex: 1, wordBreak: 'break-all' }}>{file.name}</span>
              {ext && (
                <span style={{ fontSize: '12px', background: '#eee', padding: '2px 6px', borderRadius: '3px' }}>
                  {ext}
                </span>
              )}
              <span style={{ fontSize: '12px', color: '#555' }}>{formatFileSize(file.size)}</span>
              <button
                type="button"
                onClick={() => handleRemoveNewDocument(index)}
                title="Quitar de la cola"
                style={{ background: '#dc3545', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '3px', cursor: 'pointer' }}
              >
                ✕
              </button>
            </li>
          );
        })}
      </ul>
    ) : (
      <p style={{ color: '#777', fontStyle: 'italic', margin: '0 0 12px 0' }}>No hay documentos en la cola para subir.</p>
    )}

    <input type="file" multiple onChange={handleDocumentationChange} />
  </div>

  <div className={style.formGroup}>
    <h2 className={style.title}>Motivo del cambio</h2>
    <textarea
      className={style.inputDescription}
      name="motivo"
      placeholder="Describa brevemente el motivo de los cambios (opcional)"
      value={formData.motivo || ''}
      onChange={handleChange}
      rows={3}
    />
  </div>
 
  {submitErrors.length > 0 && (
        <div className={style.errorSummary}>
          <h4 className={style.errorSummaryTitle}>⚠️ Se encontraron {submitErrors.length} error(es):</h4>
          <ul className={style.errorSummaryList}>
            {submitErrors.map((err, i) => (
              <li key={i}><strong>{getFieldLabel(err.field)}:</strong> {err.message}</li>
            ))}
          </ul>
        </div>
      )}

        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
        </button>
      </form>

      <div className={style.versioningSection}>
        <VersionHistory propertyId={id} />
        <PropertyComments propertyId={id} />
      </div>
      
      {/* Modal de clientes interesados */}
      {showPropertyClients && (
        <PropertyClients
          propertyId={id}
          onClose={() => setShowPropertyClients(false)}
        />
      )}
    </div>
  );
};

export default EditPropertyForm;
